/* eslint-disable @typescript-eslint/no-explicit-any */
import CustomPagination from "@/components/pagination/custom-pagination";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { ColumnVisibility } from "@/components/data-table/column-visiblity";
import InitialPageVisitGuideModal from "@/components/page-visit-guide";
import { LabelsDropdown } from "@/components/popups/labels-dropdown";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLabels } from "@/hooks/use-labels";
import { useTemplates } from "@/hooks/use-template";
import { cn } from "@/lib/utils";
import ManageLabels from "@/pages/labels/manage-label";
import TemplateGridView from "@/pages/templates/template-grid-view";
import TemplateTableView from "@/pages/templates/template-table-view";
import { useTemplateStore } from "@/stores/template.store";
import { LabelType } from "@/types/common";
import { TemplateContentType, TemplateType } from "@/types/template.types";
import { List } from "@phosphor-icons/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AgGridReact } from "ag-grid-react";
import { Code, Grid, Plus, Text } from "lucide-react";
import { useRef, useState } from "react";
import { usePermission } from "@/hooks/use-premission";

// Route
export const Route = createFileRoute("/_private/templates/")({
  component: RouteComponent,
});

function RouteComponent() {
  const [limit, setLimit] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [view, setView] = useState<"grid" | "list">("list");
  const [showManageLabels, setShowManageLabels] = useState(false);
  const [selectedRows, setSelectedRows] = useState<TemplateType[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);

  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const { data: labels } = useLabels(1, 100, LabelType.TEMPLATE);

  const { data, isLoading } = useTemplates(
    currentPage,
    true,
    limit,
    selectedLabels,
  );
  const { clearStoredTemplate, clearReport } = useTemplateStore();
  const gridRef = useRef<AgGridReact<TemplateType> | null>(null);

  const handleCreateTemplate = (type: TemplateContentType) => {
    clearStoredTemplate();
    clearReport();
    navigate({ to: "/templates/editor", search: { type } });
  };

  // const { mutate: addLabelsToTemplates } = useAddLabelsToTemplates();
  const onSelectionChanged = () => {
    const selectedNodes = gridRef?.current?.api.getSelectedRows();
    const selectedData = selectedNodes || [];
    setSelectedRows(selectedData);
  };

  const handleLabelsChange = (newLabels: string[]) => {
    setSelectedLabels(newLabels);
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 className="page-heading">Templates</h1>
          {/* <Button variant="destructive" size="sm">
            <Trash2 />
            Delete Selected
          </Button> */}
        </div>

        <div className="flex items-center gap-3">
          {/* <Button asChild>
            <Link
              to="/templates/editor/$templateId"
              params={{ templateId: "123" }}
              search={{ type: "html" }}
            >
              Create Template
            </Link>
          </Button> */}
          {/* <CreateTemplate /> */}

          <Tabs
            value={view}
            onValueChange={(e) => {
              setView(e as "grid" | "list");
              setSelectedRows([]);
            }}
          >
            <TabsList className="border">
              <TabsTrigger value="list" className="w-full px-2">
                <List size={16} />
              </TabsTrigger>
              <TabsTrigger value="grid" className="w-full px-2">
                <Grid size={16} />
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {hasPermission("templates:create") && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="flex items-center gap-2">
                  <Plus size={16} />
                  Create Template
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
                <DropdownMenuItem
                  onClick={() => handleCreateTemplate(TemplateContentType.HTML)}
                >
                  <Code />
                  HTML
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    handleCreateTemplate(TemplateContentType.PLAIN_TEXT)
                  }
                >
                  <Text />
                  Plain Text
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div
        className={cn(
          "flex items-center justify-between flex-wrap md:flex-nowrap gap-2 mb-4",
        )}
      >
        <div className="flex items-center gap-2">
          <LabelsDropdown
            onLabelsChange={handleLabelsChange}
            multiSelect={true}
            placeholder="Filter by labels"
            labels={
              labels?.data?.map((label) => ({
                value: label._id,
                label: label.name,
                bgcolor: label.color || "",
              })) || []
            }
            value={selectedLabels} // Pass the current selected labels
            align="start"
          />
          {selectedRows.length > 0 &&
            hasPermission("templates:edit") &&
            view === "list" && (
              <Button
                size="sm"
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => setShowManageLabels(true)}
              >
                Manage Labels
              </Button>
            )}
        </div>

        <div>
          {view === "list" && (
            <ColumnVisibility
              gridRef={gridRef}
              storageKey="template-columns-state"
            />
          )}
        </div>
      </div>

      {view === "grid" ? (
        <TemplateGridView templates={data} isLoading={isLoading} />
      ) : (
        <TemplateTableView
          ref={gridRef}
          onRowSelect={onSelectionChanged}
          templates={data}
          selectedRows={selectedRows}
          isLoading={isLoading}
        />
      )}

      {showManageLabels && (
        <ManageLabels
          open={showManageLabels}
          onOpenChange={setShowManageLabels}
          selectedRows={selectedRows.map((row) => ({
            _id: row._id,
            labels: row.labelIds || [],
          }))}
          labelType={LabelType.TEMPLATE}
          afterChange={() => {
            gridRef.current?.api.deselectAll();
            setSelectedRows([]);
            setShowManageLabels(false);
          }}
        />
      )}
      <CustomPagination
        currentPage={data?.meta.currentPage ?? 1}
        onChange={(page) => setCurrentPage(page)}
        totalPages={data?.meta.totalPages ?? 10}
        siblingsCount={1}
        showSizeSelector={false}
        className={"mt-4"}
        rowsPerPage={limit ?? 100}
        onRowsPerPageChange={(rowsPerPage) => setLimit(rowsPerPage)}
      />

      <InitialPageVisitGuideModal type="templates" />
    </div>
  );
}
